"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch } from "react";
import type { AppLocale, FunnelTemplateId, FunnelWizardState, TrafficChannel } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { CHANNEL_CATALOG, CHANNEL_GROUPS, getChannelDef, normalizeChannelId, type ChannelDef } from "@/lib/funnel-planner/channels";
import { ChannelCard } from "@/components/funnel-planner/ui/ChannelCard";
import { ChannelConfigBlock } from "@/components/funnel-planner/ui/ChannelConfigBlock";
import { CustomSelect } from "@/components/funnel-planner/ui/CustomSelect";
import {
  acquisitionModeLabel,
  channelGroupLabel,
  channelLabel,
  formatFunnelNumber,
  funnelText,
} from "@/lib/funnel-planner/i18n";
import {
  joinAuditTicketGroups,
  STEP_TWO_BATCH2_CTA_TICKETS,
  STEP_TWO_CTA_TICKETS,
  STEP_TWO_FIELD_INPUT_TICKETS,
  STEP_TWO_TRAFFIC_CHANNEL_LIST_SUPPORT_TICKETS,
  STEP_TWO_VISITOR_SLIDER_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

const TEMPLATE_PRIORITY_GROUPS: Record<FunnelTemplateId, string[]> = {
  "agency-white-label":      ["Search Organic", "Search Paid", "Content & PR", "Email & Messaging"],
  "franchise-multi-location":["Search Organic", "Search Paid", "Social Paid", "Display & Programmatic"],
  "ai-search-geo":           ["AI Search / AEO", "Search Organic", "Content & PR", "Social Organic"],
  "adult-compliance":        ["Search Organic", "Display & Programmatic", "Social Organic", "Retargeting"],
  "b2b-enterprise":          ["Search Organic", "Search Paid", "Email & Messaging", "Content & PR"],
  "ecommerce-dtc":           ["Search Paid", "Social Paid", "Email & Messaging", "Retargeting"],
  "high-ticket-coaching":    ["Search Organic", "Social Organic", "Email & Messaging", "Search Paid"],
  "saas-plg":                ["Search Organic", "Search Paid", "Content & PR", "Social Paid"],
};

const TEMPLATE_DEFAULT_CHANNELS: Record<FunnelTemplateId, string[]> = {
  "agency-white-label":      ["google_organic", "linkedin"],
  "franchise-multi-location":["google_organic", "google_ads"],
  "ai-search-geo":           ["chatgpt_search", "google_organic"],
  "adult-compliance":        ["google_organic", "display_ads"],
  "b2b-enterprise":          ["google_organic", "linkedin"],
  "ecommerce-dtc":           ["google_ads", "facebook_ads"],
  "high-ticket-coaching":    ["google_organic", "facebook_organic"],
  "saas-plg":                ["google_organic", "google_ads"],
};

const SHORT_LIST_SIZE = 10;
const TRAFFIC_CHANNEL_LIST_READINESS_TICKET =
  "P1-211001-interactive-step-02-traffic-channel-list-readiness-board";
const TRAFFIC_CHANNEL_LIST_OFFER_ASSEMBLY_TICKET =
  "P3-211002-interactive-step-02-traffic-channel-list-offer-assembly-configurator";
const VISITOR_SLIDERS_READINESS_TICKET =
  "P1-212001-interactive-step-02-visitor-sliders-readiness-board";
const VISITOR_SLIDERS_OFFER_ASSEMBLY_TICKET =
  "P3-212002-interactive-step-02-visitor-sliders-offer-assembly-configurator";
const STEP_TWO_OFFER_ASSEMBLY_CTA_TICKETS = [
  "P1-131001-cta-step-02-button-planner-action-025-offer-assembly-mini-cta-brief",
  "P1-132001-cta-step-02-button-planner-action-026-offer-assembly-mini-cta-brief",
  "P1-133001-cta-step-02-button-planner-action-027-offer-assembly-mini-cta-brief",
  "P1-134001-cta-step-02-button-planner-action-028-offer-assembly-mini-cta-brief",
  "P1-135001-cta-step-02-button-planner-action-029-offer-assembly-mini-cta-brief",
  "P1-136001-cta-step-02-button-planner-action-030-offer-assembly-mini-cta-brief",
  "P1-137001-cta-step-02-button-planner-action-031-offer-assembly-mini-cta-brief",
  "P1-138001-cta-step-02-button-planner-action-032-offer-assembly-mini-cta-brief",
  "P1-139001-cta-step-02-button-planner-action-033-offer-assembly-mini-cta-brief",
  "P1-140001-cta-step-02-button-planner-action-034-offer-assembly-mini-cta-brief",
  "P1-141001-cta-step-02-button-planner-action-035-offer-assembly-mini-cta-brief",
  "P1-142001-cta-step-02-button-planner-action-036-offer-assembly-mini-cta-brief",
  "P1-143001-cta-step-02-button-planner-action-037-offer-assembly-mini-cta-brief",
  "P1-144001-cta-step-02-button-planner-action-038-offer-assembly-mini-cta-brief",
  "P1-145001-cta-step-02-button-planner-action-039-offer-assembly-mini-cta-brief",
  "P1-146001-cta-step-02-button-planner-action-040-offer-assembly-mini-cta-brief",
  "P1-147001-cta-step-02-button-planner-action-041-offer-assembly-mini-cta-brief",
  "P1-148001-cta-step-02-button-planner-action-042-offer-assembly-mini-cta-brief",
  "P1-149001-cta-step-02-button-planner-action-043-offer-assembly-mini-cta-brief",
  "P1-150001-cta-step-02-button-planner-action-044-offer-assembly-mini-cta-brief",
  "P1-151001-cta-step-02-button-planner-action-045-offer-assembly-mini-cta-brief",
  "P1-152001-cta-step-02-button-planner-action-046-offer-assembly-mini-cta-brief",
  ...STEP_TWO_CTA_TICKETS,
  ...STEP_TWO_BATCH2_CTA_TICKETS,
] as const;
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";
const COMPACT_CHANNEL_SETTINGS_SHELLS = new Set(["mobile", "foldable", "watch", "ereader"]);

function getSortedGroups(templateId: FunnelTemplateId | null): string[] {
  if (!templateId) return CHANNEL_GROUPS;
  const priority = TEMPLATE_PRIORITY_GROUPS[templateId] ?? [];
  const rest = CHANNEL_GROUPS.filter((g) => !priority.includes(g));
  return [...priority, ...rest];
}

function offerAssemblyCtaTicketsForChannelId(channelId: string, visibleChannelIds: string[]): string {
  const channelIndex = visibleChannelIds.indexOf(channelId);
  if (channelIndex < 0 || visibleChannelIds.length === 0) return "";
  return STEP_TWO_OFFER_ASSEMBLY_CTA_TICKETS
    .filter((_, ticketIndex) => ticketIndex % visibleChannelIds.length === channelIndex)
    .join(";");
}

type Action = { type: "SET_TRAFFIC_CHANNELS"; channels: TrafficChannel[]; source?: "automatic" };

const ACQTYPE_FALLBACKS: Record<TrafficChannel["acquisitionType"], string> = {
  organic: "Organic",
  cpc: "CPC (paid per click)",
  cpm: "CPM (paid per impression)",
  cpv: "CPV (paid per view)",
  flat_fee: "Flat monthly fee",
  cpa: "CPA (paid per customer)",
  cost_per_lead: "CPL (paid per lead)",
  revenue_share: "Revenue share",
  labor_time: "Labor / time cost",
  organic_unknown: "Organic / unknown cost",
};

const ATTRIBUTION_ROLE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["attributionRole"]>; key: string; fallback: string }> = [
  { value: "first_touch", key: "trafficFirstTouchOption", fallback: "First touch" },
  { value: "last_touch", key: "trafficLastTouchOption", fallback: "Last touch" },
  { value: "assist", key: "trafficAssistOption", fallback: "Assist" },
  { value: "retargeting", key: "trafficRetargetingOption", fallback: "Retargeting" },
  { value: "direct", key: "trafficDirectOption", fallback: "Direct" },
];

const TRACKING_SETUP_OPTIONS: Array<{ value: NonNullable<TrafficChannel["trackingSetup"]>; key: string; fallback: string }> = [
  { value: "none", key: "trafficTrackingNoneOption", fallback: "None" },
  { value: "utm_only", key: "trafficTrackingUtmOnlyOption", fallback: "UTM only" },
  { value: "pixel", key: "trafficTrackingPixelOption", fallback: "Pixel" },
  { value: "server_side", key: "trafficTrackingServerSideOption", fallback: "Server-side" },
  { value: "crm_matched", key: "trafficTrackingCrmMatchedOption", fallback: "CRM matched" },
];

const AUDIENCE_TEMPERATURE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["audienceTemperature"]>; key: string; fallback: string }> = [
  { value: "cold", key: "trafficTemperatureColdOption", fallback: "Cold" },
  { value: "warm", key: "trafficTemperatureWarmOption", fallback: "Warm" },
  { value: "hot", key: "trafficTemperatureHotOption", fallback: "Hot" },
];

const DEVICE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["device"]>; key: string; fallback: string }> = [
  { value: "all", key: "trafficDeviceAllOption", fallback: "All devices" },
  { value: "desktop", key: "trafficDeviceDesktopOption", fallback: "Desktop" },
  { value: "mobile", key: "trafficDeviceMobileOption", fallback: "Mobile" },
  { value: "tablet", key: "trafficDeviceTabletOption", fallback: "Tablet" },
];

const RAMP_TYPE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["rampType"]>; key: string; fallback: string }> = [
  { value: "flat", key: "trafficRampFlatOption", fallback: "Flat" },
  { value: "linear", key: "trafficRampLinearOption", fallback: "Linear" },
  { value: "seasonal", key: "trafficRampSeasonalOption", fallback: "Seasonal" },
  { value: "launch_spike", key: "trafficRampLaunchSpikeOption", fallback: "Launch spike" },
  { value: "decay", key: "trafficRampDecayOption", fallback: "Decay" },
];

const CONFIDENCE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["confidence"]>; key: string; fallback: string }> = [
  { value: "low", key: "trafficConfidenceLowOption", fallback: "Low" },
  { value: "medium", key: "trafficConfidenceMediumOption", fallback: "Medium" },
  { value: "high", key: "trafficConfidenceHighOption", fallback: "High" },
];

const LABEL_S: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "#94a3b8",
  display: "block",
  marginBottom: 4,
};

const INPUT_S: React.CSSProperties = {
  width: "100%",
  borderRadius: 6,
  border: "1px solid #334155",
  background: "rgba(255,255,255,0.05)",
  padding: "6px 10px",
  fontSize: 13,
  color: "#e2e8f0",
  outline: "none",
  boxSizing: "border-box",
};

function parseOptionalFloat(raw: string): number | undefined {
  return raw === "" ? undefined : parseFloat(raw);
}

function channelFromDefinition(def: ChannelDef, content: FunnelPlannerContent): TrafficChannel {
  return {
    id: def.id,
    label: channelLabel(content, def.id, def.label),
    monthlyVisitors: Math.max(def.defaultVisitors, 1),
    acquisitionType: def.defaultAcquisitionType,
    ...(def.defaultCpc != null ? { cpc: def.defaultCpc } : {}),
    ...(def.defaultCostPerView != null ? { costPerView: def.defaultCostPerView } : {}),
    ...(def.defaultCpm != null ? { cpm: def.defaultCpm } : {}),
    ...(def.defaultFlatMonthlyFee != null ? { flatMonthlyFee: def.defaultFlatMonthlyFee } : {}),
    ...(def.defaultCostPerLead != null ? { costPerLead: def.defaultCostPerLead } : {}),
    ...(def.defaultCostPerAcquisition != null ? { costPerAcquisition: def.defaultCostPerAcquisition } : {}),
    ...(def.defaultRevenueSharePercent != null ? { revenueSharePercent: def.defaultRevenueSharePercent } : {}),
    ...(def.defaultLaborHoursPerMonth != null ? { laborHoursPerMonth: def.defaultLaborHoursPerMonth } : {}),
    ...(def.defaultHourlyCost != null ? { hourlyCost: def.defaultHourlyCost } : {}),
    ...(def.intentLevel != null ? { intentLevel: def.intentLevel } : {}),
    ...(def.funnelPosition != null ? { funnelPosition: def.funnelPosition } : {}),
    ...(def.trackingConfidencePct != null ? { trackingConfidencePct: def.trackingConfidencePct } : {}),
    utmSource: def.id,
    utmMedium: def.defaultAcquisitionType === "organic" ? "organic" : "paid",
    attributionRole: def.funnelPosition === "retargeting" ? "retargeting" : "first_touch",
    trackingSetup: def.trackingConfidencePct != null && def.trackingConfidencePct >= 70 ? "pixel" : "utm_only",
    audienceTemperature: def.intentLevel ?? "cold",
    device: "all",
    rampType: "flat",
    confidence: def.trackingConfidencePct != null && def.trackingConfidencePct >= 75 ? "high" : "medium",
  };
}

const INPUT_FOCUS_HANDLERS = {
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.outline = "2px solid #3b82f6";
    e.currentTarget.style.outlineOffset = "2px";
  },
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.outline = "none";
    e.currentTarget.style.outlineOffset = "0";
  },
};

interface UndoEntry {
  channel: TrafficChannel;
  index: number;
}

export function TrafficChannelsStep({
  state,
  dispatch,
  content,
  locale,
}: {
  state: FunnelWizardState;
  dispatch: Dispatch<Action>;
  content: FunnelPlannerContent;
  locale: AppLocale;
}) {
  const channels = state.trafficChannels;
  const totalVisitors = channels.reduce((sum, c) => sum + c.monthlyVisitors, 0);
  const hasTrafficChannels = channels.length > 0;
  const hasVisitorVolumes = totalVisitors > 0;
  const currencyCode = state.regionalSettings.currency;
  const monetaryLabel = (key: string, fallback: string) =>
    funnelText(content, key, fallback).replace(/\bUSD\b/g, currencyCode);
  const acqTypeOptions = useMemo(
    () =>
      (Object.keys(ACQTYPE_FALLBACKS) as Array<TrafficChannel["acquisitionType"]>).map((value) => ({
        value,
        label: acquisitionModeLabel(content, value, ACQTYPE_FALLBACKS[value]),
      })),
    [content],
  );

  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const [undoVisible, setUndoVisible] = useState(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [touchedInputs, setTouchedInputs] = useState<Set<string>>(new Set());
  const [showAllChannels, setShowAllChannels] = useState(false);
  const [expandedChannelIds, setExpandedChannelIds] = useState<Set<string>>(new Set());
  const [usesInlineChannelSettings, setUsesInlineChannelSettings] = useState(false);

  const prevLengthRef = useRef(channels.length);
  const configPanelRef = useRef<HTMLDivElement | null>(null);
  const configBlockRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const pickerFocusRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 720px)");
    const update = () => {
      const shell = document.body.getAttribute("data-device-shell");
      setUsesInlineChannelSettings(media.matches || COMPACT_CHANNEL_SETTINGS_SHELLS.has(shell ?? ""));
    };
    update();
    media.addEventListener("change", update);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ["data-device-shell"] });
    return () => {
      media.removeEventListener("change", update);
      observer.disconnect();
    };
  }, []);

  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || channels.length > 0 || !state.templateId) return;
    seededRef.current = true;
    const defaults = TEMPLATE_DEFAULT_CHANNELS[state.templateId] ?? [];
    const seeded: TrafficChannel[] = defaults.flatMap((id) => {
      const def = getChannelDef(id);
      if (!def) return [];
      return [channelFromDefinition(def, content)];
    });
    if (seeded.length > 0) dispatch({ type: "SET_TRAFFIC_CHANNELS", channels: seeded, source: "automatic" });
  }, [state.templateId, channels.length, dispatch, content]);

  const sortedGroups = useMemo(() => getSortedGroups(state.templateId), [state.templateId]);

  const shortListIds = useMemo((): Set<string> => {
    if (!state.templateId) {
      return new Set(CHANNEL_CATALOG.slice(0, SHORT_LIST_SIZE).map((c) => c.id));
    }
    const priority = TEMPLATE_PRIORITY_GROUPS[state.templateId] ?? [];
    const ids: string[] = [];
    for (const grp of priority) {
      const grpChannels = CHANNEL_CATALOG.filter((c) => c.group === grp);
      for (const c of grpChannels) {
        if (ids.length < SHORT_LIST_SIZE) ids.push(c.id);
      }
    }
    if (ids.length < SHORT_LIST_SIZE) {
      for (const c of CHANNEL_CATALOG) {
        if (ids.length >= SHORT_LIST_SIZE) break;
        if (!ids.includes(c.id)) ids.push(c.id);
      }
    }
    return new Set(ids);
  }, [state.templateId]);

  const visibleChannelIds = useMemo(() => {
    const selectedIds = new Set(channels.map((channel) => normalizeChannelId(channel.id)));
    return sortedGroups.flatMap((group) => {
      const groupChannels = CHANNEL_CATALOG.filter((channel) => channel.group === group);
      const visibleChannels = showAllChannels
        ? groupChannels
        : groupChannels.filter((channel) => shortListIds.has(channel.id) || selectedIds.has(normalizeChannelId(channel.id)));
      return visibleChannels.map((channel) => channel.id);
    });
  }, [channels, shortListIds, showAllChannels, sortedGroups]);

  useEffect(() => {
    if (channels.length > prevLengthRef.current) {
      const lastChannel = channels[channels.length - 1];
      const block = configBlockRefs.current.get(lastChannel.id);
      if (block) {
        block.scrollIntoView({ behavior: "smooth", block: "nearest" });
        const firstInput = block.querySelector<HTMLInputElement>("input");
        if (firstInput) firstInput.focus();
      }
    }
    prevLengthRef.current = channels.length;
  }, [channels]);

  const getActive = useCallback(
    (id: string) => channels.find((c) => normalizeChannelId(c.id) === normalizeChannelId(id)),
    [channels],
  );

  const handleToggle = useCallback(
    (id: string) => {
      const existingIndex = channels.findIndex((c) => normalizeChannelId(c.id) === normalizeChannelId(id));
      if (existingIndex !== -1) {
        const removed = channels[existingIndex];
        const remaining = channels.filter((c) => c.id !== id);
        setExpandedChannelIds((prev) => {
          const next = new Set(prev);
          next.delete(normalizeChannelId(removed.id));
          return next;
        });

        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoEntry({ channel: removed, index: existingIndex });
        setUndoVisible(true);
        undoTimerRef.current = setTimeout(() => {
          setUndoVisible(false);
          setUndoEntry(null);
        }, 5000);

        dispatch({ type: "SET_TRAFFIC_CHANNELS", channels: remaining });

        setTimeout(() => {
          if (remaining.length > 0) {
            const nextId = remaining[Math.min(existingIndex, remaining.length - 1)].id;
            const nextBlock = configBlockRefs.current.get(nextId);
            if (nextBlock) {
              const firstInput = nextBlock.querySelector<HTMLInputElement>("input");
              if (firstInput) firstInput.focus();
            }
          } else if (pickerFocusRef.current) {
            pickerFocusRef.current.focus();
          }
        }, 0);
      } else {
        const def = getChannelDef(id);
        if (!def) return;
        setExpandedChannelIds((prev) => {
          const next = new Set(prev);
          next.add(normalizeChannelId(id));
          return next;
        });
        dispatch({ type: "SET_TRAFFIC_CHANNELS", channels: [...channels, channelFromDefinition(def, content)] });
      }
    },
    [channels, dispatch, content],
  );

  const handleChannelCardToggle = useCallback(
    (id: string) => {
      if (usesInlineChannelSettings && getActive(id)) {
        const normalized = normalizeChannelId(id);
        setExpandedChannelIds((prev) => {
          const next = new Set(prev);
          if (next.has(normalized)) next.delete(normalized);
          else next.add(normalized);
          return next;
        });
        return;
      }
      handleToggle(id);
    },
    [getActive, handleToggle, usesInlineChannelSettings],
  );

  const handleUndo = useCallback(() => {
    if (!undoEntry) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    const restored = [...channels];
    restored.splice(undoEntry.index, 0, undoEntry.channel);
    setExpandedChannelIds((prev) => {
      const next = new Set(prev);
      next.add(normalizeChannelId(undoEntry.channel.id));
      return next;
    });
    dispatch({ type: "SET_TRAFFIC_CHANNELS", channels: restored });
    setUndoVisible(false);
    setUndoEntry(null);
  }, [undoEntry, channels, dispatch]);

  const handleChange = useCallback(
    (updated: TrafficChannel) => {
      dispatch({
        type: "SET_TRAFFIC_CHANNELS",
        channels: channels.map((c) => (c.id === updated.id ? updated : c)),
      });
    },
    [channels, dispatch],
  );

  const markTouched = useCallback((inputId: string) => {
    setTouchedInputs((prev) => {
      const next = new Set(prev);
      next.add(inputId);
      return next;
    });
  }, []);

  const setConfigBlockRef = useCallback((id: string, element: HTMLDivElement | null) => {
    if (element) configBlockRefs.current.set(id, element);
    else configBlockRefs.current.delete(id);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {!usesInlineChannelSettings && (
        <a
          href="#channel-config-panel"
          style={{
            position: "absolute",
            left: -9999,
            top: "auto",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
          onFocus={(e) => { e.currentTarget.style.left = "0"; e.currentTarget.style.width = "auto"; e.currentTarget.style.height = "auto"; }}
          onBlur={(e) => { e.currentTarget.style.left = "-9999px"; e.currentTarget.style.width = "1px"; e.currentTarget.style.height = "1px"; }}
        >
          {funnelText(content, "trafficSkipToConfig", "Skip to channel configuration")}
        </a>
      )}

      {undoVisible && undoEntry && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            background: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 8,
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 13,
            color: "#e2e8f0",
            boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          <span>{funnelText(content, "trafficRemovedChannel", "Removed {channel}", { channel: undoEntry.channel.label })}</span>
          <button
            type="button"
            onClick={handleUndo}
            style={{
              background: "rgba(37,99,235,0.15)",
              border: "1px solid #2563eb",
              borderRadius: 4,
              color: "#93c5fd",
              fontSize: 12,
              fontWeight: 600,
              padding: "3px 10px",
              cursor: "pointer",
              minWidth: 44,
              minHeight: 28,
            }}
          >
            {funnelText(content, "trafficUndoButton", "Undo")}
          </button>
        </div>
      )}

      <div
        data-funnel-planner-traffic-layout="true"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) min(320px, 100%)",
          gap: 24,
          alignItems: "start",
        }}
      >
        {/* ── LEFT: channel picker ── */}
        <div
          ref={pickerFocusRef}
          tabIndex={-1}
          data-funnel-planner-traffic-channel-list="true"
          data-audit-ticket={joinAuditTicketGroups(
            TRAFFIC_CHANNEL_LIST_READINESS_TICKET,
            TRAFFIC_CHANNEL_LIST_OFFER_ASSEMBLY_TICKET,
            STEP_TWO_TRAFFIC_CHANNEL_LIST_SUPPORT_TICKETS,
          )}
          data-tier-one-family="readiness_board"
          data-gamification-current-state={hasTrafficChannels ? "complete_result" : "active_inprogress"}
          data-gamification-reduced-motion-static="supported"
          data-gamification-supported-states={READINESS_SUPPORTED_STATES}
          data-readiness-ready-count={hasTrafficChannels ? 1 : 0}
          data-readiness-total-count={1}
          data-readiness-tier={hasTrafficChannels ? "traffic_channels_ready" : "traffic_channels_needed"}
          data-blockers-bucket={hasTrafficChannels ? "none" : "one"}
          style={{ display: "flex", flexDirection: "column", gap: 0, outline: "none" }}
        >
          <span className="sr-only" role="status" aria-live="polite">
            {hasTrafficChannels
              ? funnelText(content, "gamificationTrafficChannelsReady", "Traffic channel list ready")
              : funnelText(content, "gamificationTrafficChannelsWaiting", "Traffic channel list waiting for at least one channel")}
          </span>
          {sortedGroups.map((group) => {
            const groupChannels = CHANNEL_CATALOG.filter((c) => c.group === group);
            const visibleChannels = showAllChannels
              ? groupChannels
              : groupChannels.filter((c) => shortListIds.has(c.id) || channels.some((ch) => ch.id === c.id));
            if (visibleChannels.length === 0) return null;
            return (
              <div key={group} style={{ marginBottom: 14 }}>
                <p style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  marginBottom: 6,
                  marginTop: 0,
                  borderTop: "1px solid #1e293b",
                  paddingTop: 10,
                }}>
                  {channelGroupLabel(content, group)}
                </p>
                <div
                  data-funnel-planner-traffic-channel-grid="true"
                  style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}
                >
                  {visibleChannels.map((def) => {
                    const activeChannel = getActive(def.id);
                    const auditTicket = offerAssemblyCtaTicketsForChannelId(def.id, visibleChannelIds);
                    const normalizedId = normalizeChannelId(def.id);
                    const expanded = Boolean(activeChannel && expandedChannelIds.has(normalizedId));
                    return (
                      <div key={def.id} data-funnel-planner-traffic-channel-row="true">
                        <ChannelCard
                          def={{ ...def, label: channelLabel(content, def.id, def.label) }}
                          channel={activeChannel}
                          onToggle={handleChannelCardToggle}
                          recommendedLabel={funnelText(content, "recommendedBadge", "Recommended")}
                          popularLabel={funnelText(content, "popularBadge", "Popular")}
                          auditTicket={auditTicket}
                          showExpandToggle={usesInlineChannelSettings && Boolean(activeChannel)}
                          expanded={expanded}
                          expandControlsId={`channel-config-${normalizedId}`}
                          expandLabel={funnelText(content, "trafficConfigRegionLabel", "Channel configuration")}
                          offerAssemblyCtaLabel={
                            activeChannel
                              ? (content.gamificationOfferAssemblyCtaSelected as string)
                              : (content.gamificationOfferAssemblyCtaScopeThis as string)
                          }
                          offerAssemblyCurrentState={activeChannel ? "complete_result" : "active_inprogress"}
                          offerAssemblyStatusLabel={
                            activeChannel
                              ? (content.gamificationOfferAssemblyCtaSelected as string)
                              : (content.gamificationOfferAssemblyCtaEmptyStatus as string)
                          }
                        />
                        {usesInlineChannelSettings && activeChannel && expanded && (
                          <ChannelConfigBlock
                            id={`channel-config-${normalizedId}`}
                            channel={activeChannel}
                            content={content}
                            currencyCode={currencyCode}
                            isLast
                            mode="inline"
                            onChange={handleChange}
                            onRemove={handleToggle}
                            onTouched={markTouched}
                            setBlockRef={setConfigBlockRef}
                            touchedInputs={touchedInputs}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => setShowAllChannels((v) => !v)}
            style={{
              background: "transparent",
              border: "1px solid #334155",
              borderRadius: 6,
              color: "#94a3b8",
              fontSize: 12,
              fontWeight: 500,
              padding: "7px 14px",
              cursor: "pointer",
              marginTop: 4,
              textAlign: "center",
              minHeight: 44,
            }}
            aria-pressed={showAllChannels}
            aria-label={showAllChannels
              ? funnelText(content, "trafficShowFewerChannels", "Show fewer channels")
              : funnelText(content, "trafficShowAllChannels", "Show all channels")}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#e2e8f0"; (e.currentTarget as HTMLButtonElement).style.borderColor = "#475569"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#94a3b8"; (e.currentTarget as HTMLButtonElement).style.borderColor = "#334155"; }}
          >
            {showAllChannels
              ? funnelText(content, "trafficShowFewerChannels", "Show fewer channels")
              : funnelText(content, "trafficShowAllChannels", "Show all channels")}
          </button>

          {channels.length === 0 && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 12, color: "#94a3b8", padding: "4px 0", marginBottom: 4 }}>
                {content.channelNoneSelected}
              </p>
              <p style={{ fontSize: 11, color: "#475569", padding: "4px 0" }}>
                {funnelText(content, "trafficExampleEmpty", "Example: Search Organic - 5,000 visitors/mo")}
              </p>
            </div>
          )}
        </div>

        {/* ── RIGHT: sticky config panel ── */}
        {!usesInlineChannelSettings && (
        <div
          id="channel-config-panel"
          ref={configPanelRef}
          role="region"
          aria-label={funnelText(content, "trafficConfigRegionLabel", "Channel configuration")}
          data-funnel-planner-channel-config-panel="true"
          data-funnel-planner-visitor-sliders="true"
          data-audit-ticket={joinAuditTicketGroups(
            VISITOR_SLIDERS_READINESS_TICKET,
            VISITOR_SLIDERS_OFFER_ASSEMBLY_TICKET,
            STEP_TWO_FIELD_INPUT_TICKETS,
            STEP_TWO_VISITOR_SLIDER_SUPPORT_TICKETS,
          )}
          data-tier-one-family="readiness_board"
          data-gamification-current-state={hasVisitorVolumes ? "complete_result" : hasTrafficChannels ? "partial" : "active_inprogress"}
          data-gamification-reduced-motion-static="supported"
          data-gamification-supported-states={READINESS_SUPPORTED_STATES}
          data-readiness-ready-count={hasVisitorVolumes ? 1 : 0}
          data-readiness-total-count={1}
          data-readiness-tier={hasVisitorVolumes ? "visitor_volume_ready" : "visitor_volume_needed"}
          data-blockers-bucket={hasVisitorVolumes ? "none" : "one"}
          style={{
            position: "sticky",
            top: 24,
            maxHeight: "calc(100vh - 80px)",
            overflowY: "auto",
            borderRadius: 12,
            border: "1px solid #1e293b",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <span className="sr-only" role="status" aria-live="polite">
            {hasVisitorVolumes
              ? funnelText(content, "gamificationVisitorVolumesReady", "Visitor volumes mapped")
              : funnelText(content, "gamificationVisitorVolumesWaiting", "Visitor volume inputs waiting")}
          </span>
          {/* Panel header */}
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", margin: 0 }}>
              {funnelText(content, "trafficConfigureChannelsTitle", "Configure channels")}
            </h3>
            {channels.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, color: "#64748b" }}>
                  {funnelText(content, "trafficSelectedCount", "{count} selected", { count: channels.length })}
                </span>
                <span
                  aria-live="polite"
                  aria-atomic="true"
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#93c5fd",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatFunnelNumber(totalVisitors, locale)} {funnelText(content, "perMonthLabel", "/mo")}
                </span>
              </div>
            )}
          </div>

          {/* Empty state */}
          {channels.length === 0 && (
            <div style={{
              padding: "32px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              textAlign: "center",
            }}>
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#334155" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-6-6m0 0l6-6m-6 6h12" />
              </svg>
              <p style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>
                {funnelText(content, "trafficEmptyConfig", "Click a channel on the left to set monthly volumes")}
              </p>
            </div>
          )}

          {/* Per-channel config */}
          {channels.map((ch, i) => {
            const def = getChannelDef(ch.id);
            if (!def) return null;
            const visitorsId = `${ch.id}-visitors`;
            const cpcId = `${ch.id}-cpc`;
            const cpvId = `${ch.id}-cpv`;
            const cpmId = `${ch.id}-cpm`;
            const ctrId = `${ch.id}-ctr`;
            const flatFeeId = `${ch.id}-flat-fee`;
            const cpaId = `${ch.id}-cpa`;
            const cplId = `${ch.id}-cpl`;
            const revenueShareId = `${ch.id}-revenue-share`;
            const laborHoursId = `${ch.id}-labor-hours`;
            const hourlyCostId = `${ch.id}-hourly-cost`;
            const campaignId = `${ch.id}-campaign`;
            const visitorsInvalid = touchedInputs.has(visitorsId) && ch.monthlyVisitors === 0;
            return (
              <div
                key={ch.id}
                ref={(el) => {
                  if (el) configBlockRefs.current.set(ch.id, el);
                  else configBlockRefs.current.delete(ch.id);
                }}
                style={{
                  padding: "12px 14px",
                  borderBottom: i < channels.length - 1 ? "1px solid #0f172a" : "none",
                }}
              >
                {/* Channel label + remove */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span
                      aria-hidden="true"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 26,
                        height: 20,
                        borderRadius: 4,
                        background: "rgba(37,99,235,0.20)",
                        fontSize: 9,
                        fontWeight: 700,
                        color: "#93c5fd",
                        padding: "0 3px",
                        letterSpacing: "-0.02em",
                        flexShrink: 0,
                      }}
                    >
                      {def.icon}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#cbd5e1" }}>
                      {def.label}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle(ch.id)}
                    aria-label={funnelText(content, "trafficRemoveChannelAria", "Remove {channel}", {
                      channel: channelLabel(content, ch.id, ch.label),
                    })}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      fontSize: 16,
                      lineHeight: 1,
                      padding: "10px 12px",
                      minWidth: 44,
                      minHeight: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      borderRadius: 4,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
                      (e.currentTarget as HTMLButtonElement).style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = "#94a3b8";
                      (e.currentTarget as HTMLButtonElement).style.textDecoration = "none";
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Visitors + Type */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div>
                    <label htmlFor={visitorsId} style={LABEL_S}>{funnelText(content, "trafficVisitorsLabel", "Visitors /mo")}</label>
                    <input
                      id={visitorsId}
                      type="number"
                      min={0}
                      max={10_000_000}
                      value={ch.monthlyVisitors || ""}
                      placeholder={funnelText(content, "trafficVisitorsPlaceholder", "e.g. 5000")}
                      onChange={(e) =>
                        handleChange({ ...ch, monthlyVisitors: Math.max(0, parseInt(e.target.value) || 0) })
                      }
                      onBlur={(e) => {
                        markTouched(visitorsId);
                        INPUT_FOCUS_HANDLERS.onBlur(e);
                      }}
                      onFocus={INPUT_FOCUS_HANDLERS.onFocus}
                      style={{
                        ...INPUT_S,
                        border: visitorsInvalid ? "1px solid #f59e0b" : "1px solid #334155",
                      }}
                      aria-describedby={visitorsInvalid ? `${visitorsId}-err` : undefined}
                    />
                    {visitorsInvalid && (
                      <p id={`${visitorsId}-err`} style={{ fontSize: 10, color: "#f59e0b", marginTop: 3 }}>
                        {funnelText(content, "trafficVisitorsRequired", "Enter visitors to continue")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`${ch.id}-type`} style={LABEL_S}>{funnelText(content, "trafficAcquisitionModelLabel", "Acquisition model")}</label>
                    <CustomSelect
                      id={`${ch.id}-type`}
                      value={ch.acquisitionType}
                      onChange={(v) => handleChange({ ...ch, acquisitionType: v as TrafficChannel["acquisitionType"] })}
                      options={acqTypeOptions}
                    />
                  </div>
                </div>

                {ch.acquisitionType === "cpc" && (
                  <div style={{ marginBottom: 8 }}>
                    <label htmlFor={cpcId} style={LABEL_S}>{monetaryLabel("trafficCostPerClickLabel", "Cost per click (USD)")}</label>
                    <input
                      id={cpcId}
                      type="number"
                      min={0}
                      step={0.01}
                      value={ch.cpc ?? ""}
                      placeholder="e.g. 2.50"
                      onChange={(e) => handleChange({ ...ch, cpc: parseOptionalFloat(e.target.value) })}
                      {...INPUT_FOCUS_HANDLERS}
                      style={INPUT_S}
                    />
                  </div>
                )}

                {ch.acquisitionType === "cpv" && (
                  <div style={{ marginBottom: 8 }}>
                    <label htmlFor={cpvId} style={LABEL_S}>{monetaryLabel("trafficCostPerViewLabel", "Cost per view (USD)")}</label>
                    <input
                      id={cpvId}
                      type="number"
                      min={0}
                      step={0.01}
                      value={ch.costPerView ?? ""}
                      placeholder="0.08"
                      onChange={(e) => handleChange({ ...ch, costPerView: parseOptionalFloat(e.target.value) })}
                      {...INPUT_FOCUS_HANDLERS}
                      style={INPUT_S}
                    />
                  </div>
                )}

                {ch.acquisitionType === "cpm" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <div>
                      <label htmlFor={cpmId} style={LABEL_S}>{monetaryLabel("trafficCpmLabel", "CPM (USD)")}</label>
                      <input
                        id={cpmId}
                        type="number"
                        min={0}
                        step={0.01}
                        value={ch.cpm ?? ""}
                        placeholder="5.00"
                        onChange={(e) => handleChange({ ...ch, cpm: parseOptionalFloat(e.target.value) })}
                        {...INPUT_FOCUS_HANDLERS}
                        style={INPUT_S}
                      />
                    </div>
                    <div>
                      <label htmlFor={ctrId} style={LABEL_S}>{funnelText(content, "trafficCtrLabel", "CTR (%)")}</label>
                      <input
                        id={ctrId}
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={ch.ctr ?? ""}
                        placeholder="1.5"
                        onChange={(e) => { const v = parseOptionalFloat(e.target.value); handleChange({ ...ch, ctr: v == null ? undefined : Math.min(100, v) }); }}
                        {...INPUT_FOCUS_HANDLERS}
                        style={INPUT_S}
                      />
                    </div>
                  </div>
                )}

                {ch.acquisitionType === "flat_fee" && (
                  <div style={{ marginBottom: 8 }}>
                    <label htmlFor={flatFeeId} style={LABEL_S}>{monetaryLabel("trafficFlatFeeLabel", "Flat monthly fee (USD)")}</label>
                    <input
                      id={flatFeeId}
                      type="number"
                      min={0}
                      step={1}
                      value={ch.flatMonthlyFee ?? ""}
                      placeholder="1500"
                      onChange={(e) => handleChange({ ...ch, flatMonthlyFee: parseOptionalFloat(e.target.value) })}
                      {...INPUT_FOCUS_HANDLERS}
                      style={INPUT_S}
                    />
                  </div>
                )}

                {ch.acquisitionType === "cpa" && (
                  <div style={{ marginBottom: 8 }}>
                    <label htmlFor={cpaId} style={LABEL_S}>{monetaryLabel("trafficCostPerCustomerLabel", "Cost per customer (USD)")}</label>
                    <input
                      id={cpaId}
                      type="number"
                      min={0}
                      step={1}
                      value={ch.costPerAcquisition ?? ""}
                      placeholder="45"
                      onChange={(e) => handleChange({ ...ch, costPerAcquisition: parseOptionalFloat(e.target.value) })}
                      {...INPUT_FOCUS_HANDLERS}
                      style={INPUT_S}
                    />
                  </div>
                )}

                {ch.acquisitionType === "cost_per_lead" && (
                  <div style={{ marginBottom: 8 }}>
                    <label htmlFor={cplId} style={LABEL_S}>{monetaryLabel("trafficCostPerLeadLabel", "Cost per lead (USD)")}</label>
                    <input
                      id={cplId}
                      type="number"
                      min={0}
                      step={0.01}
                      value={ch.costPerLead ?? ""}
                      placeholder="12.50"
                      onChange={(e) => handleChange({ ...ch, costPerLead: parseOptionalFloat(e.target.value) })}
                      {...INPUT_FOCUS_HANDLERS}
                      style={INPUT_S}
                    />
                  </div>
                )}

                {ch.acquisitionType === "revenue_share" && (
                  <div style={{ marginBottom: 8 }}>
                    <label htmlFor={revenueShareId} style={LABEL_S}>{funnelText(content, "trafficRevenueShareLabel", "Revenue share (%)")}</label>
                    <input
                      id={revenueShareId}
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={ch.revenueSharePercent ?? ""}
                      placeholder="20"
                      onChange={(e) => {
                        const v = parseOptionalFloat(e.target.value);
                        handleChange({ ...ch, revenueSharePercent: v == null ? undefined : Math.min(100, v) });
                      }}
                      {...INPUT_FOCUS_HANDLERS}
                      style={INPUT_S}
                    />
                  </div>
                )}

                {ch.acquisitionType === "labor_time" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <div>
                      <label htmlFor={laborHoursId} style={LABEL_S}>{funnelText(content, "trafficLaborHoursLabel", "Hours / mo")}</label>
                      <input
                        id={laborHoursId}
                        type="number"
                        min={0}
                        step={0.5}
                        value={ch.laborHoursPerMonth ?? ""}
                        placeholder="12"
                        onChange={(e) => handleChange({ ...ch, laborHoursPerMonth: parseOptionalFloat(e.target.value) })}
                        {...INPUT_FOCUS_HANDLERS}
                        style={INPUT_S}
                      />
                    </div>
                    <div>
                      <label htmlFor={hourlyCostId} style={LABEL_S}>{monetaryLabel("trafficHourlyCostLabel", "Hourly cost (USD)")}</label>
                      <input
                        id={hourlyCostId}
                        type="number"
                        min={0}
                        step={1}
                        value={ch.hourlyCost ?? ""}
                        placeholder="75"
                        onChange={(e) => handleChange({ ...ch, hourlyCost: parseOptionalFloat(e.target.value) })}
                        {...INPUT_FOCUS_HANDLERS}
                        style={INPUT_S}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor={campaignId} style={LABEL_S}>
                    {funnelText(content, "trafficCampaignLabel", "Campaign")} <span style={{ color: "#64748b" }}>({funnelText(content, "optionalLabel", "optional")})</span>
                  </label>
                  <input
                    id={campaignId}
                    type="text"
                    value={ch.campaignName ?? ""}
                    maxLength={120}
                    placeholder={funnelText(content, "trafficCampaignPlaceholder", "e.g. Q3 awareness")}
                    onChange={(e) => handleChange({ ...ch, campaignName: e.target.value || undefined })}
                    {...INPUT_FOCUS_HANDLERS}
                    style={INPUT_S}
                  />
                </div>
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#93c5fd", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {funnelText(content, "trafficAdvancedTrackingLabel", "Advanced tracking")}
                  </summary>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
                    <div>
                      <label htmlFor={`${ch.id}-attribution`} style={LABEL_S}>{funnelText(content, "trafficAttributionRoleLabel", "Attribution role")}</label>
                      <CustomSelect
                        id={`${ch.id}-attribution`}
                        value={ch.attributionRole ?? "first_touch"}
                        onChange={(v) => handleChange({ ...ch, attributionRole: v as TrafficChannel["attributionRole"] })}
                        options={ATTRIBUTION_ROLE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-tracking`} style={LABEL_S}>{funnelText(content, "trafficTrackingSetupLabel", "Tracking setup")}</label>
                      <CustomSelect
                        id={`${ch.id}-tracking`}
                        value={ch.trackingSetup ?? "utm_only"}
                        onChange={(v) => handleChange({ ...ch, trackingSetup: v as TrafficChannel["trackingSetup"] })}
                        options={TRACKING_SETUP_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-temperature`} style={LABEL_S}>{funnelText(content, "trafficAudienceTemperatureLabel", "Audience temperature")}</label>
                      <CustomSelect
                        id={`${ch.id}-temperature`}
                        value={ch.audienceTemperature ?? ch.intentLevel ?? "cold"}
                        onChange={(v) => handleChange({ ...ch, audienceTemperature: v as TrafficChannel["audienceTemperature"] })}
                        options={AUDIENCE_TEMPERATURE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-device`} style={LABEL_S}>{funnelText(content, "trafficDeviceLabel", "Device")}</label>
                      <CustomSelect
                        id={`${ch.id}-device`}
                        value={ch.device ?? "all"}
                        onChange={(v) => handleChange({ ...ch, device: v as TrafficChannel["device"] })}
                        options={DEVICE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-geo`} style={LABEL_S}>{funnelText(content, "trafficGeoLabel", "Geo / market")}</label>
                      <input
                        id={`${ch.id}-geo`}
                        type="text"
                        maxLength={80}
                        value={ch.geo ?? ""}
                        placeholder={funnelText(content, "trafficGeoPlaceholder", "e.g. US, DACH, Tel Aviv")}
                        onChange={(e) => handleChange({ ...ch, geo: e.target.value || undefined })}
                        {...INPUT_FOCUS_HANDLERS}
                        style={INPUT_S}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-budget-cap`} style={LABEL_S}>{funnelText(content, "trafficBudgetCapLabel", "Budget cap")}</label>
                      <input
                        id={`${ch.id}-budget-cap`}
                        type="number"
                        min={0}
                        value={ch.monthlyBudgetCap ?? ""}
                        onChange={(e) => handleChange({ ...ch, monthlyBudgetCap: parseOptionalFloat(e.target.value) })}
                        {...INPUT_FOCUS_HANDLERS}
                        style={INPUT_S}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-ramp`} style={LABEL_S}>{funnelText(content, "trafficRampTypeLabel", "Ramp type")}</label>
                      <CustomSelect
                        id={`${ch.id}-ramp`}
                        value={ch.rampType ?? "flat"}
                        onChange={(v) => handleChange({ ...ch, rampType: v as TrafficChannel["rampType"] })}
                        options={RAMP_TYPE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${ch.id}-confidence`} style={LABEL_S}>{funnelText(content, "trafficConfidenceLabel", "Confidence")}</label>
                      <CustomSelect
                        id={`${ch.id}-confidence`}
                        value={ch.confidence ?? "medium"}
                        onChange={(v) => handleChange({ ...ch, confidence: v as TrafficChannel["confidence"] })}
                        options={CONFIDENCE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
                      />
                    </div>
                  </div>
                </details>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
